import { type FC } from "react";
import { isRouteErrorResponse, useRouteError } from "react-router";

import { AccessDenied } from "@styled/access_denied";
import { NotFound } from "@styled/not_found";

const AUTH_ERROR_PATTERNS = ["not allowed to", "Not authorized"];

// Every `organization.<resource>.find_by(id:)` miss in QueryType raises this,
// so it covers a deleted record and one belonging to another organization
// alike — the server deliberately does not distinguish the two.
const NOT_FOUND_PATTERN = "Missing id=";

const isAuthorizationError = (error: unknown): boolean => {
  if (isRouteErrorResponse(error) && error.status === 403) {
    return true;
  }

  if (error instanceof Error) {
    return AUTH_ERROR_PATTERNS.some((pattern) =>
      error.message.includes(pattern),
    );
  }

  return false;
};

const isNotFoundError = (error: unknown): boolean => {
  if (isRouteErrorResponse(error) && error.status === 404) {
    return true;
  }

  if (error instanceof Error) {
    return error.message.includes(NOT_FOUND_PATTERN);
  }

  return false;
};

export const DashboardErrorBoundary: FC = () => {
  const error = useRouteError();

  if (isAuthorizationError(error)) {
    return <AccessDenied />;
  }

  if (isNotFoundError(error)) {
    return <NotFound />;
  }

  throw error;
};
